import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AudienceWithCompletionCriteria,
  ExperienceAnchorType,
  ExperienceTemplateStatus,
  IMomentTemplate,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import arrayMove from 'array-move';
import { cloneDeep, partition, sortBy } from 'lodash';
import { ChevronDown, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsHeader } from '../../../../../../components/DialogActionsHeader';
import { DialogContentWithoutPadding } from '../../../../../../components/DialogContentWithoutPadding';
import { DiscardChangesDialog } from '../../../../../../components/DiscardChangesDialog';
import { InfoBox } from '../../../../../../components/InfoBox';
import { InfoCircle } from '../../../../../../components/InfoCircle';
import { TokenDefinition } from '../../../../../../components/MarkdownText';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  getAudienceName,
  getRandomAudienceColor
} from '../../../../../../services/experiences/audience';
import { updateDataInDoc } from '../../../../../../services/firebase';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { useProductAnalytics } from '../../../../../../services/productAnalytics';
import { DragDropListEmpty } from '../../../../../Experiences/components/Builders/components/DragDropList';
import {
  useContentBlocks,
  useSubTaskBlocks
} from '../../../../../Experiences/components/Builders/TaskBuilder';
import { DROP_IDS } from '../../../../../Experiences/components/Builders/TaskBuilder/components/dndIds';
import { MessageContainer } from '../../../../../Experiences/components/Builders/TaskBuilder/components/Messages';
import { TaskBuilderContext } from '../../../../../Experiences/components/Builders/TaskBuilder/types';
import { TaskBuilderBlockValidationState } from '../../../../../Experiences/components/Builders/TaskBuilder/validation';
import {
  getBlocksForMomentAudience,
  getEmptyAudience
} from '../../../../services';
import { EditComponentProps, ViewComponentProps } from '../../types';
import { AudienceDrawer } from '../Audience';
import {
  BlockList,
  BlockListReadOnly,
  CopyAudienceContentButton,
  getCopyContentSourceOptions
} from './BlockList';
import { BlockPreview } from './BlockPreview';
import { SendMomentTestButton } from './SendMomentTestButton';
import { denormalizeBlocksForPreview } from './service';

const SIDE_CONTAINER_MIN_WIDTH = 318;
const SIDE_CONTAINER_MAX_WIDTH = 400;
const MAIN_CONTAINER_MIN_WIDTH = 580;

const Grid = styled('div')<{
  left?: { min: number; max: number };
  center?: { min: number };
  right?: { min: number; max: number };
}>((p) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(${MAIN_CONTAINER_MIN_WIDTH}px, 1fr) minmax(${SIDE_CONTAINER_MIN_WIDTH}px, ${SIDE_CONTAINER_MAX_WIDTH}px)`,
  gridTemplateRows: '100%',
  height: '100%',
  width: '100%'
}));

const SideContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(4)}px ${p.theme.spacing(3)}px`,
  height: '100%',
  width: '100%',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column'
}));

const BlockPreviewContainer = styled(SideContainer)((p) => ({
  padding: `${p.theme.spacing(4)}px ${p.theme.spacing(3)}px`,
  backgroundColor: p.theme.custom.colors.gray.w100,
  borderRight: p.theme.custom.border.standard
}));

const PreviewContainer = styled(SideContainer)((p) => ({
  padding: `${p.theme.spacing(0)}px`
}));

const PreviewBody = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  overflow: 'auto'
}));

const NoAudienceContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center'
}));

export const AudienceSelectorContainer = styled('div')((p) => ({
  backgroundColor: p.theme.palette.background.paper,
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  borderRight: p.theme.custom.border.standard,
  borderBottom: p.theme.custom.border.standard
}));

const BORDER_SIZE = '3px';
const AudienceItem = styled('div')<{ selected: boolean }>((p) => ({
  fontWeight: p.selected ? p.theme.typography.fontWeightBold : undefined,
  borderBottom: `${BORDER_SIZE} solid ${
    p.selected ? p.theme.custom.colors.green.w400 : 'transparent'
  }`,
  cursor: 'pointer'
}));

const BlockPreviewAudienceEmptyStateContainer = styled('div')((p) => ({
  margin: `${p.theme.spacing(3)}px auto`,
  maxWidth: 300,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'column',
  color: p.theme.custom.colors.gray.w700
}));

const BlockPreviewAudienceEmptyState = ({
  selectedAudienceId,
  audiences,
  onCopyAllBlocksFromOneAudienceToAnother
}: {
  selectedAudienceId: string;
  audiences: IMomentTemplate['content']['audiences'];
  onCopyAllBlocksFromOneAudienceToAnother: (
    sourceAudienceId: string,
    destinationAudienceId: string
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const copyContentSourceOptions = getCopyContentSourceOptions(
    audiences,
    selectedAudienceId,
    t
  );

  return (
    <BlockPreviewAudienceEmptyStateContainer>
      {copyContentSourceOptions.length ? (
        <FlexContainer direction="column" fullWidth spacing={1}>
          <Typography variant="body2">
            {t('Copy content from another audience')}
          </Typography>
          <CopyAudienceContentButton
            selectedAudienceId={selectedAudienceId}
            options={copyContentSourceOptions}
            onCopyAllBlocksFromOneAudienceToAnother={
              onCopyAllBlocksFromOneAudienceToAnother
            }
          />
        </FlexContainer>
      ) : (
        <DragDropListEmpty>
          {t('Drag content or sub-tasks here')}
        </DragDropListEmpty>
      )}
    </BlockPreviewAudienceEmptyStateContainer>
  );
};

const DeletedAudiencesDropdown = ({
  audiences,
  onSelectAudience,
  hasOtherAudiences,
  style
}: {
  audiences: AudienceWithCompletionCriteria[];
  onSelectAudience: (audienceId: string) => void;
  hasOtherAudiences: boolean;
  style?: React.CSSProperties;
}) => {
  const { t } = useUnsafeTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);
  if (!audiences.length) {
    return null;
  }
  return (
    <>
      <Button
        ref={ref}
        size="small"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen((x) => !x)}
        variant="text"
        style={style}
      >
        {t('Deleted audiences')}
      </Button>
      <Menu
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {audiences.map((a) => (
          <MenuItem
            key={a.id}
            onClick={() => {
              setOpen(false);
              onSelectAudience(a.id);
            }}
          >
            {getAudienceName(a, hasOtherAudiences, t, {
              hideInactiveLabel: true
            })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const CopyInactiveContentToOtherAudiencesButton = ({
  otherAudiences,
  onCopy
}: {
  onCopy: (destinationAudienceId: string) => void;
  otherAudiences: AudienceWithCompletionCriteria[];
}) => {
  const { t } = useUnsafeTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen(true)}
        ref={buttonRef}
      >
        {t('Select audience')}
      </Button>
      <Menu
        getContentAnchorEl={null}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {otherAudiences.map((a) => (
          <MenuItem
            onClick={() => {
              onCopy(a.id);
              setOpen(false);
            }}
          >
            <ListItemText>
              {getAudienceName(a, otherAudiences.length > 1, t)}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const InactiveAudienceInfoBoxContainer = styled('div')((p) => ({
  paddingTop: p.theme.spacing(2),
  paddingBottom: p.theme.spacing(2),
  background: p.theme.custom.colors.gray.w100
}));

const InactiveAudienceInfoBox = ({
  inactiveAudience,
  otherAudiences,
  onCopyContent,
  onRestoreAudience,
  hasOtherAudiences
}: {
  inactiveAudience: AudienceWithCompletionCriteria;
  otherAudiences: AudienceWithCompletionCriteria[];
  onCopyContent: (destinationAudienceId: string) => void;
  onRestoreAudience: () => void;
  hasOtherAudiences: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <InactiveAudienceInfoBoxContainer>
      <MessageContainer>
        <InfoBox>
          <FlexContainer justifyContent="space-between" alignItems="flex-start">
            <FlexContainer alignItems="flex-start">
              <div style={{ marginTop: 2 }}>
                <InfoCircle size={18} />
              </div>
              <div>
                <LightMdToJsx component="div">
                  {t(
                    'You are previewing content for the deleted **{{ audience }}** audience.',
                    {
                      audience: getAudienceName(
                        inactiveAudience,
                        hasOtherAudiences,
                        t,
                        {
                          hideInactiveLabel: true
                        }
                      )
                    }
                  )}
                </LightMdToJsx>
                <LightMdToJsx component="div">
                  {t('This Moment will not be sent to this audience.')}
                </LightMdToJsx>
                <br />
                <FlexContainer>
                  <div>{t('Copy this content to')}</div>
                  <CopyInactiveContentToOtherAudiencesButton
                    onCopy={onCopyContent}
                    otherAudiences={otherAudiences}
                  />
                </FlexContainer>
              </div>
            </FlexContainer>
            <Button
              size="small"
              onClick={() => onRestoreAudience()}
              variant="text"
              color="primary"
            >
              {t('Restore')}
            </Button>
          </FlexContainer>
        </InfoBox>
      </MessageContainer>
    </InactiveAudienceInfoBoxContainer>
  );
};

const PreviewSection = ({
  mode,
  value,
  org,

  onChange,

  selectedAudience,
  onSelectAudience,

  selectedBlock,
  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlockInAudience,
  onCopyAllBlocksFromOneAudienceToAnother,
  validation,
  tokens,
  builderContext
}: {
  mode: 'edit' | 'view';
  value: Doc<IMomentTemplate>;
  org: IOrganization;

  onChange: EditComponentProps['onChange'];

  selectedAudience: string | null;
  onSelectAudience: (nextAudience: string | null) => void;
  selectedBlock: string | null;
  onSelectBlock: (nextSelected: string | null) => void;
  onDeleteBlocks: (blockIds: string[]) => void;
  onDuplicateBlockInAudience: (audienceId: string, blockId: string) => void;
  onCopyAllBlocksFromOneAudienceToAnother: (
    sourceAudienceId: string,
    destinationAudienceId: string
  ) => void;
  validation: TaskBuilderBlockValidationState;
  tokens: TokenDefinition[];
  builderContext: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const { tz, content } = value.data;
  const { blocks, audiences: allAudiences } = content;
  const [audiences, inactiveAudiences] = partition(
    allAudiences,
    (a) => !a.inactive
  );
  const [
    newAudienceForDrawer,
    setNewAudienceForDrawer
  ] = useState<AudienceWithCompletionCriteria | null>(null);
  const theme = useTheme();
  const openAudienceDrawer = () =>
    setNewAudienceForDrawer(
      getEmptyAudience(() => getRandomAudienceColor(theme))
    );
  const closeAudienceDrawer = () => setNewAudienceForDrawer(null);
  const denormalizedBlocks = useMemo(() => {
    const audience = allAudiences.find((a) => a.id === selectedAudience);
    const audienceBlocks = getBlocksForMomentAudience(audience, blocks);
    return denormalizeBlocksForPreview(audienceBlocks, org, tz);
  }, [selectedAudience, blocks, allAudiences, org, tz]);

  const hasOtherAudiences = audiences.length > 1;
  const selectedAudienceItem = allAudiences.find(
    (a) => a.id === selectedAudience
  );

  const restoreAudience = (audience: AudienceWithCompletionCriteria) => {
    const newAudiences = allAudiences.map((a) =>
      a.id === audience.id
        ? {
            ...a,
            inactive: false
          }
        : a
    );

    onChange({
      ...value,
      data: {
        ...value.data,
        content: {
          ...value.data.content,
          audiences: newAudiences
        }
      }
    });
  };

  return (
    <PreviewContainer onClick={() => selectedBlock && onSelectBlock(null)}>
      <AudienceSelectorContainer>
        <FlexContainer justifyContent="space-between" alignItems="flex-start">
          <FlexContainer spacing={2}>
            {sortBy(audiences, (a) => a.rule === 'EVERYONE').map((a) => (
              <AudienceItem
                key={a.id}
                selected={a.id === selectedAudience}
                role="button"
                onClick={() => onSelectAudience(a.id)}
              >
                {getAudienceName(a, hasOtherAudiences, t)}
              </AudienceItem>
            ))}
            {mode === 'edit' && (
              <>
                {!!inactiveAudiences.length && (
                  <DeletedAudiencesDropdown
                    style={{ borderBottom: `${BORDER_SIZE} solid transparent` }}
                    audiences={inactiveAudiences}
                    onSelectAudience={onSelectAudience}
                    hasOtherAudiences={allAudiences.length > 1}
                  />
                )}
                <Button
                  // this border is only used to align the item towards
                  // the one above - otherwise it looks off center
                  style={{ borderBottom: `${BORDER_SIZE} solid transparent` }}
                  startIcon={<Plus size={16} />}
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={openAudienceDrawer}
                >
                  {t('Add audience')}
                </Button>
              </>
            )}
          </FlexContainer>
          <SendMomentTestButton d={value} org={org} />
        </FlexContainer>
        {mode === 'edit' && newAudienceForDrawer && (
          <AudienceDrawer
            value={value}
            onChange={(value) => {
              onChange(value);
              onSelectAudience(newAudienceForDrawer.id);
            }}
            open={true}
            onClose={closeAudienceDrawer}
            audience={newAudienceForDrawer}
          />
        )}
      </AudienceSelectorContainer>
      {selectedAudienceItem?.inactive && (
        <InactiveAudienceInfoBox
          inactiveAudience={selectedAudienceItem}
          otherAudiences={audiences}
          onCopyContent={(destinationAudienceId) => {
            onCopyAllBlocksFromOneAudienceToAnother(
              selectedAudienceItem.id,
              destinationAudienceId
            );
            onSelectAudience(destinationAudienceId);
          }}
          hasOtherAudiences={allAudiences.length > 1}
          onRestoreAudience={() => restoreAudience(selectedAudienceItem)}
        />
      )}
      <PreviewBody>
        <BlockPreviewContainer>
          {!!audiences.length ? (
            <BlockPreview
              blocks={denormalizedBlocks}
              org={org}
              builderContext={builderContext}
              selectedBlock={selectedBlock}
              onSelectBlock={onSelectBlock}
              onDeleteBlocks={mode === 'edit' ? onDeleteBlocks : undefined}
              onDuplicateBlock={
                mode === 'edit' && onDuplicateBlockInAudience
                  ? (blockId) =>
                      selectedAudience &&
                      onDuplicateBlockInAudience(selectedAudience, blockId)
                  : undefined
              }
              tokens={tokens}
              validation={validation}
              draggable={mode === 'edit'}
              emptyState={
                <BlockPreviewAudienceEmptyState
                  selectedAudienceId={selectedAudience || ''}
                  audiences={allAudiences}
                  onCopyAllBlocksFromOneAudienceToAnother={(
                    sourceAudienceId,
                    destinationAudienceId
                  ) => {
                    const sourceAudience = value.data.content.audiences.find(
                      (a) => a.id === sourceAudienceId
                    );
                    const destinationAudience = value.data.content.audiences.find(
                      (a) => a.id === destinationAudienceId
                    );

                    // Should never happen
                    if (!sourceAudience) return;
                    if (!destinationAudience) return;

                    // Always true; copy content button is disabled if there’s just one audience
                    const hasOtherAudiences = true;

                    trackEvent({
                      eventType: PAEventType.OTHER,
                      name: 'Moment Content Copied',
                      Explanation:
                        'Admin copied content for a moment from one audience to another',
                      'Moment ID': value.id,
                      'Moment Name': value.data.title,
                      'Selected Target Audience': getAudienceName(
                        destinationAudience,
                        hasOtherAudiences,
                        t
                      ),
                      'Copied Target Audience': getAudienceName(
                        sourceAudience,
                        hasOtherAudiences,
                        t
                      ),
                      'Content Empty': denormalizedBlocks.length === 0
                    });

                    onCopyAllBlocksFromOneAudienceToAnother(
                      sourceAudienceId,
                      destinationAudienceId
                    );
                  }}
                />
              }
            />
          ) : (
            <NoAudienceContainer>
              {t(
                'You need to have an audience created before you can continue editing'
              )}
            </NoAudienceContainer>
          )}
        </BlockPreviewContainer>
      </PreviewBody>
    </PreviewContainer>
  );
};

const EXCLUDED_SUBTASK_TYPES = [
  'DATA_FIELD_CHANGE',
  'QUESTION_supportingActor',
  'CALENDLY_INVITE',
  'SWAG_UP_CHOICE',
  'SUBMIT',
  'DOCUSIGN_ENVELOPE_SIGN'
];

const INCLUDED_INTEGRATIONS: Integration[] = [];

export const EditSectionContent = ({
  value: model,
  onChange: setModel,
  onCancel,
  onSave,
  hasChanges,
  tokens
}: EditComponentProps) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const discardDialog = useDialogState(false);
  const { trackEvent } = useProductAnalytics();
  const [validation, setValidation] = useState<TaskBuilderBlockValidationState>(
    {}
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const { blocks, audiences } = model.data.content;
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) ?? null,
    [blocks, selectedBlockId]
  );
  const onSelectBlock = (nextBlockId: string | null) =>
    setSelectedBlockId(nextBlockId);

  const [selectedAudienceId, setSelectedAudienceId] = useState(
    audiences.filter((a) => !a.inactive)[0]?.id || null
  );

  const selectedAudience = useMemo(
    () => audiences.find((a) => a.id === selectedAudienceId) || null,
    [audiences, selectedAudienceId]
  );

  const subTaskBlocks = useSubTaskBlocks(
    EMPTY_ARR,
    NOOP,
    EXCLUDED_SUBTASK_TYPES,
    INCLUDED_INTEGRATIONS
  );
  const contentBlocks = useContentBlocks({
    extendedSelection: true
  });

  const {
    onAddBlockToAudience,
    onChangeBlockOrderInAudience,
    onChangeBlock,
    onCopyAllBlocksFromOneAudienceToAnother,
    onDuplicateBlockInAudience,
    onDeleteBlocks
  } = useMemo(() => {
    const changeContent = (
      changeFn: (
        prev: IMomentTemplate['content']
      ) => Partial<IMomentTemplate['content']>
    ) => {
      return setModel((v) =>
        updateDataInDoc(v, {
          content: {
            ...v.data.content,
            ...changeFn(v.data.content)
          }
        })
      );
    };
    const addBlockToAudience = (
      prev: IMomentTemplate['content'],
      audienceId: string,
      nextBlock: TaskBlockTemplate,
      insertIndex: number
    ): IMomentTemplate['content'] => {
      return {
        blocks: [...prev.blocks, nextBlock],
        audiences: prev.audiences.map((a) => {
          if (a.id !== audienceId) {
            return a;
          }
          const nextBlocks = [...a.blocks];
          nextBlocks.splice(insertIndex, 0, nextBlock.id);
          return {
            ...a,
            blocks: nextBlocks
          };
        })
      };
    };
    return {
      onChangeBlockOrderInAudience: (
        audienceId: string,
        nextOrder: string[]
      ) => {
        changeContent((prev) => ({
          audiences: prev.audiences.map((a) =>
            a.id === audienceId
              ? {
                  ...a,
                  blocks: nextOrder
                }
              : a
          )
        }));
      },
      onChangeBlock: (nextBlock: TaskBlockTemplate) => {
        changeContent((prev) => ({
          blocks: prev.blocks.map((b) =>
            b.id === nextBlock.id ? nextBlock : b
          )
        }));
      },
      onDeleteBlocks: (blockIds: string[]) => {
        changeContent((prev) => ({
          blocks: prev.blocks.filter((b) => !blockIds.includes(b.id)),
          audiences: prev.audiences.map((a) => ({
            ...a,
            blocks: a.blocks.filter((b) => !blockIds.includes(b))
          }))
        }));
      },
      onAddBlockToAudience: (
        audienceId: string,
        nextBlock: TaskBlockTemplate,
        insertIndex: number
      ) =>
        changeContent((prev) =>
          addBlockToAudience(prev, audienceId, nextBlock, insertIndex)
        ),

      onDuplicateBlockInAudience: (audienceId: string, blockId: string) => {
        changeContent((prev) => {
          const block = prev.blocks.find((b) => b.id === blockId);
          if (!block) {
            return prev;
          }
          const duplicate = cloneDeep(block);
          duplicate.id = shortid();
          const audience = prev.audiences.find((a) => a.id === audienceId);
          if (!audience) {
            return prev;
          }
          const i = audience.blocks.indexOf(block.id);
          if (i === -1) {
            return prev;
          }
          return addBlockToAudience(prev, audienceId, duplicate, i + 1);
        });
      },
      onCopyAllBlocksFromOneAudienceToAnother: (
        sourceAudienceId: string,
        destinationAudienceId: string
      ) => {
        changeContent((prev) => {
          const sourceAudience = prev.audiences.find(
            (a) => a.id === sourceAudienceId
          );
          const destAudience = prev.audiences.find(
            (a) => a.id === destinationAudienceId
          );
          if (!sourceAudience || !destAudience) {
            return prev;
          }
          const newBlocks = getBlocksForMomentAudience(
            sourceAudience,
            prev.blocks
          ).map<TaskBlockTemplate>((b) => ({
            ...cloneDeep(b),
            id: shortid()
          }));
          const nextAudience = {
            ...destAudience,
            blocks: [...destAudience.blocks, ...newBlocks.map((b) => b.id)]
          };
          return {
            audiences: prev.audiences.map((a) =>
              a.id === nextAudience.id ? nextAudience : a
            ),
            blocks: [...prev.blocks, ...newBlocks]
          };
        });
      }
    };
  }, [setModel]);

  const context: TaskBuilderContext = useMemo(() => {
    return {
      templateId: model.id,
      orgId: model.data.orgId,
      title: model.data.title,
      adminTitle: model.data.title,
      contacts: [],
      notificationSender: {
        name: model.data.from.name || '',
        replyTo: model.data.replyTo?.email || ''
      },
      status: ExperienceTemplateStatus.DRAFT,
      supportingActors: {},
      anchor: {
        type: ExperienceAnchorType.FIXED,
        ts: model.data.sendDate.at,
        offset: {
          type: 'ABSOLUTE',
          duration: 0,
          hour: 9
        },
        autoEnrollmentRejectionThreshold: null,
        v: 2
      },
      orgOverrides: model.data.overrides?.org || null,
      tz: model.data.tz,
      tasks: EMPTY_ARR
    };
  }, [model]);

  const tryClose = () => {
    if (hasChanges) {
      discardDialog.openDialog();
      return;
    }
    onCancel();
  };

  return (
    <>
      <Dialog open={true} fullScreen onClose={tryClose}>
        <DialogActionsHeader
          left={
            <Typography variant="h4" component="h1">
              {t('Edit content')}
            </Typography>
          }
          onClose={tryClose}
        >
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending={t('Saving...')}
            onClick={() => {
              trackEvent({
                eventType: PAEventType.OTHER,
                name: 'Moment Content Saved',
                Explanation:
                  'Admin user saves their changes to the content of a Moment',
                'Moment ID': model.id,
                'Moment Name': model.data.title
              });
              return onSave();
            }}
            disabled={!hasChanges}
          >
            {t('Save')}
          </ButtonWithPromise>
        </DialogActionsHeader>
        <DialogContentWithoutPadding fullHeight>
          <DragDropContext
            onDragEnd={(r) => {
              if (!selectedAudience) {
                return;
              }

              if (r.destination?.droppableId === DROP_IDS.blockOrder) {
                if (r.source.droppableId === DROP_IDS.blockOrder) {
                  onChangeBlockOrderInAudience(
                    selectedAudience.id,
                    arrayMove(
                      selectedAudience.blocks,
                      r.source.index,
                      r.destination.index
                    )
                  );
                }

                if (r.source.droppableId === DROP_IDS.blockSelectorContent) {
                  const newContentBlock =
                    contentBlocks
                      .find((b) => b.key === r.draggableId)
                      ?.emptyBlock() || null;
                  if (newContentBlock) {
                    onAddBlockToAudience(
                      selectedAudience.id,
                      newContentBlock,
                      r.destination.index
                    );
                    setSelectedBlockId(newContentBlock.id);
                  }
                }

                if (r.source.droppableId === DROP_IDS.blockSelectorSubTasks) {
                  const newSubTaskBlock =
                    subTaskBlocks
                      .find((b) => b.key === r.draggableId)
                      ?.emptyBlock() || null;
                  if (newSubTaskBlock) {
                    onAddBlockToAudience(
                      selectedAudience.id,
                      newSubTaskBlock,
                      r.destination.index
                    );
                    setSelectedBlockId(newSubTaskBlock.id);
                  }
                }
              }
            }}
          >
            <Grid>
              <PreviewSection
                mode="edit"
                value={model}
                onChange={setModel}
                org={org}
                selectedAudience={selectedAudienceId}
                onSelectAudience={setSelectedAudienceId}
                selectedBlock={selectedBlockId}
                onSelectBlock={onSelectBlock}
                onDeleteBlocks={onDeleteBlocks}
                onDuplicateBlockInAudience={onDuplicateBlockInAudience}
                onCopyAllBlocksFromOneAudienceToAnother={
                  onCopyAllBlocksFromOneAudienceToAnother
                }
                builderContext={context}
                validation={validation}
                tokens={tokens}
              />
              <BlockList
                context={context}
                org={org}
                selectedBlock={selectedBlock}
                validation={validation}
                setValidation={setValidation}
                template={model}
                onDeselectBlock={() => setSelectedBlockId(null)}
                onChangeBlock={onChangeBlock}
                contentBlocks={contentBlocks}
                subTaskBlocks={subTaskBlocks}
                selectedAudience={selectedAudienceId}
                onCopyAllBlocksFromOneAudienceToAnother={
                  onCopyAllBlocksFromOneAudienceToAnother
                }
                tokens={tokens}
              />
            </Grid>
          </DragDropContext>
        </DialogContentWithoutPadding>
      </Dialog>
      <DiscardChangesDialog
        open={discardDialog.dialogOpen}
        onCancel={discardDialog.closeDialog}
        onConfirm={onCancel}
      />
    </>
  );
};

export const MomentContentPreviewDialog = ({
  d,
  open,
  onClose,
  tokens
}: {
  d: Doc<IMomentTemplate>;
  open: boolean;
  onClose: () => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const { blocks } = d.data.content;
  const audiences = d.data.content.audiences.filter((a) => !a.inactive);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) ?? null,
    [blocks, selectedBlockId]
  );
  const onSelectBlock = (nextBlockId: string | null) =>
    setSelectedBlockId(nextBlockId);

  const [selectedAudienceId, setSelectedAudienceId] = useState(
    audiences[0]?.id || null
  );

  const context: TaskBuilderContext = useMemo(() => {
    return {
      templateId: d.id,
      orgId: d.data.orgId,
      title: d.data.title,
      adminTitle: d.data.title,
      contacts: [],
      notificationSender: {
        name: d.data.from.name || '',
        replyTo: d.data.replyTo?.email || ''
      },
      status: ExperienceTemplateStatus.DRAFT,
      supportingActors: {},
      anchor: {
        type: ExperienceAnchorType.FIXED,
        ts: d.data.sendDate.at,
        offset: {
          type: 'ABSOLUTE',
          duration: 0,
          hour: 9
        },
        autoEnrollmentRejectionThreshold: null,
        v: 2
      },
      tz: d.data.tz,
      orgOverrides: d.data.overrides?.org || null,
      tasks: EMPTY_ARR
    };
  }, [d]);

  return (
    <Dialog open={true} fullScreen>
      <DialogActionsHeader
        left={
          <Typography variant="h4" component="h1">
            {t('View content')}
          </Typography>
        }
        onClose={onClose}
      ></DialogActionsHeader>
      <DialogContentWithoutPadding fullHeight>
        <DragDropContext onDragEnd={() => null}>
          <Grid>
            <PreviewSection
              mode="view"
              value={d}
              onChange={NOOP}
              org={org}
              selectedAudience={selectedAudienceId}
              onSelectAudience={setSelectedAudienceId}
              selectedBlock={selectedBlockId}
              onSelectBlock={onSelectBlock}
              onDeleteBlocks={NOOP}
              onDuplicateBlockInAudience={NOOP}
              onCopyAllBlocksFromOneAudienceToAnother={NOOP}
              builderContext={context}
              validation={{}}
              tokens={tokens}
            />
            <BlockListReadOnly
              context={context}
              org={org}
              selectedBlock={selectedBlock}
              onDeselectBlock={() => setSelectedBlockId(null)}
              tokens={tokens}
            />
          </Grid>
        </DragDropContext>
      </DialogContentWithoutPadding>
    </Dialog>
  );
};

export const ViewSectionContent = ({
  value,
  onCancel,
  tokens
}: ViewComponentProps) => {
  return (
    <MomentContentPreviewDialog
      d={value}
      open={true}
      onClose={onCancel}
      tokens={tokens}
    />
  );
};
